/**
 * HygieneForm
 * CCH Care — Phase 3
 *
 * Changes:
 * - Removed inline incomplete warning (now handled by ADLSignatureModal)
 * - Added onDocumentedCountChange callback so ADLFlowSheetPage can pass
 *   live counts to ADLSignatureModal for the warning step
 *
 * N/A wiring (2026-06-08):
 * - Consumes the care-plan isNA flag. When the section is flagged
 *   not-applicable the card renders locked with the N/A banner and the
 *   task list is hidden, matching Repositioning/SkinChecks. Hygiene has
 *   no nothing-to-report attestation, so ADLSectionNA is rendered only
 *   in the N/A case.
 */

import { useCallback, useEffect } from 'react';
import { HYGIENE_TASKS, ASSIST_LEVELS, COLORS } from '../../config/constants';
import { saveHygiene } from '../../api/adl';
import useADLForm from '../../hooks/useADLForm';
import ADLSectionCard from '../../components/ui/ADLSectionCard';
import ADLAmendmentBanner from '../../components/ui/ADLAmendmentBanner';
import ADLSectionNA from '../../components/ui/ADLSectionNA';

export default function HygieneForm({
  adlRecordId, initialData = [], sectionStatus, amendments,
  onSign, disabled, onDocumentedCountChange,
  // Phase 5d N/A wiring
  isNA = false, naReason = '',
}) {

  const buildInitial = () => {
    const state = {};
    HYGIENE_TASKS.forEach(t => {
      const existing = initialData.find(r => r.task_type === t.key);
      state[t.key] = {
        assistance_level: existing?.assistance_level ?? null,
        notes:            existing?.notes ?? '',
      };
    });
    return state;
  };

  const saveFn = useCallback(async (formData) => {
    const entries = HYGIENE_TASKS
      .filter(t => formData[t.key]?.assistance_level)
      .map(t => ({
        task_type:        t.key,
        assistance_level: formData[t.key].assistance_level,
        notes:            formData[t.key].notes || null,
      }));
    if (entries.length > 0) {
      await saveHygiene(adlRecordId, entries);
    }
  }, [adlRecordId]);

  const { formData, setFields, saveStatus } = useADLForm(buildInitial(), saveFn, { disabled });

  function setTask(taskKey, field, value) {
    setFields({ [taskKey]: { ...formData[taskKey], [field]: value } });
  }

  // Completion tracking
  const completedCount = HYGIENE_TASKS.filter(t => formData[t.key]?.assistance_level).length;
  const totalCount     = HYGIENE_TASKS.length;
  const allComplete    = completedCount === totalCount;

  // Notify parent whenever count changes so modal warning stays in sync
  useEffect(() => {
    onDocumentedCountChange?.(completedCount);
  }, [completedCount, onDocumentedCountChange]);

  return (
    <ADLSectionCard
      title="Hygiene"
      icon="🛁"
      sectionKey="hygiene"
      status={isNA ? 'na' : sectionStatus}
      saveStatus={saveStatus}
      summary={isNA ? undefined : `${completedCount}/${totalCount} tasks`}
      hasAmendments={amendments?.some(a => a.section === 'hygiene')}
      onSign={isNA ? undefined : onSign}
      disabled={disabled || isNA}
    >
      <ADLAmendmentBanner amendments={amendments} section="hygiene" />

      {/* N/A banner (care plan flag) — Hygiene has no attestation path */}
      {isNA && (
        <ADLSectionNA
          section="hygiene"
          isNA={isNA}
          naReason={naReason}
          supportsAttestation={false}
          isSectionSigned={sectionStatus === 'signed'}
        />
      )}

      {/* Progress indicator */}
      {!isNA && !disabled && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: allComplete ? COLORS.accentLight : COLORS.bg,
          border: `1px solid ${allComplete ? COLORS.accentAlpha : COLORS.border}`,
          borderRadius: 8, padding: '8px 14px', marginBottom: 12,
          transition: 'all 0.2s',
        }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: allComplete ? COLORS.accent : COLORS.textMuted }}>
            {allComplete ? '✅ All tasks documented' : `${completedCount} of ${totalCount} tasks documented`}
          </span>
          <span style={{ fontSize: 13, fontWeight: 800, color: allComplete ? COLORS.accent : COLORS.primary }}>
            {completedCount}/{totalCount}
          </span>
        </div>
      )}

      {!isNA && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {HYGIENE_TASKS.map(task => {
            const isTaskComplete = !!formData[task.key]?.assistance_level;
            return (
              <div key={task.key} style={{
                background: COLORS.bg, borderRadius: 10, padding: '12px 14px',
                border: `1px solid ${isTaskComplete ? COLORS.accentAlpha : COLORS.border}`,
                transition: 'border-color 0.15s',
              }}>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  marginBottom: 10,
                }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: COLORS.text }}>
                    {task.icon} {task.label}
                  </span>
                  {isTaskComplete && (
                    <span style={{ fontSize: 11, color: COLORS.accent, fontWeight: 700 }}>✓</span>
                  )}
                </div>

                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                  {ASSIST_LEVELS.map(level => {
                    const selected = formData[task.key]?.assistance_level === level.code;
                    return (
                      <button
                        key={level.code}
                        onClick={() => !disabled && setTask(task.key, 'assistance_level', selected ? null : level.code)}
                        style={{
                          padding: '6px 14px', borderRadius: 20,
                          fontSize: 12, fontWeight: 600, cursor: disabled ? 'default' : 'pointer',
                          background: selected ? level.color : COLORS.surface,
                          color: selected ? '#fff' : COLORS.textMuted,
                          border: `1px solid ${selected ? level.color : COLORS.border}`,
                          transition: 'all 0.15s',
                        }}
                      >
                        {level.code} — {level.label}
                      </button>
                    );
                  })}
                </div>

                {formData[task.key]?.assistance_level && (
                  <input
                    type="text"
                    value={formData[task.key]?.notes ?? ''}
                    onChange={e => !disabled && setTask(task.key, 'notes', e.target.value)}
                    placeholder="Notes (optional)"
                    disabled={disabled}
                    style={{
                      width: '100%', padding: '7px 10px', borderRadius: 6,
                      border: `1px solid ${COLORS.border}`, fontSize: 13,
                      color: COLORS.text, boxSizing: 'border-box', background: COLORS.surface,
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </ADLSectionCard>
  );
}